import { Divider, Grid, Typography, makeStyles } from "@material-ui/core";
import React, { Fragment, useMemo } from "react";

import EvalRowInput from "./EvalRowInput";
import classNames from "classnames";
import { get } from "lodash";
import { useFormState } from "react-final-form";

const useSchema = (config, categoryId, subCategoryId, subCategoryParamId) => {
  const schema = useMemo(() => {
    let output = config?.eval_categories || [];
    if (categoryId !== null) {
      output = output.filter((c) => c.id === categoryId);
    }
    if (subCategoryId !== null) {
      output = output.map((c) => ({
        ...c,
        eval_sub_categories: c.eval_sub_categories.filter(
          (sc) => sc.id === subCategoryId
        ),
      }));
    }
    if (subCategoryParamId !== null) {
      output = output.map((c) => ({
        ...c,
        eval_sub_categories: c.eval_sub_categories.map((sc) => ({
          ...sc,
          eval_sub_category_params: sc.eval_sub_category_params.filter(
            (scp) => scp.id === subCategoryParamId
          ),
        })),
      }));
    }
    return output;
  }, [categoryId, subCategoryId, subCategoryParamId, config.eval_categories]);
  return schema;
};

const usePoints = (config, schema, formState) => {
  const evalItems = useMemo(
    () => get(formState.values, "research_project_eval_items", []),
    [formState]
  );
  const points = useMemo(
    () =>
      schema.reduce(
        (points, category) => ({
          ...points,
          ...category.eval_sub_categories.reduce(
            (subPoints, subCategory) => ({
              ...subPoints,
              [subCategory.id]: ((subCategory) => {
                const item = evalItems.find(
                  (i) => i.eval_sub_category_id === subCategory.id
                );
                if (item) {
                  const judgment = config.eval_judgments.find(
                    (j) => j.id === item.eval_judgment_id
                  );
                  if (judgment) {
                    if (subCategory.id >= 21 && judgment.id === 2) {
                      return judgment.points - 5;
                    } else if (subCategory.id >= 21 && judgment.id > 2) {
                      return judgment.points - 10;
                    } else {
                      return judgment.points;
                    }
                  }

                  return 0;
                }
                return 0;
              })(subCategory),
            }),
            {}
          ),
        }),
        {}
      ),
    [config, schema, evalItems]
  );
  return points;
};
const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  padding: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  param: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  paramSelect: {
    marginTop: theme.spacing(2),
  },
  paramSelectTwoLines: {
    marginTop: theme.spacing(10),
  },
  paramPoints: {
    fontSize: 20,
    padding: theme.spacing(2),
    paddingBottom: 0,
    marginTop: theme.spacing(2),
    fontWeight: "bold",
  },
  paramPointsTwoLines: {
    marginTop: theme.spacing(10),
  },
  title: {
    marginTop: theme.spacing(4),
  },
  subTitle: {
    marginTop: theme.spacing(2),
    fontSize: 20,
  },
  notes: {
    marginTop: theme.spacing(2),
  },
  total: {
    fontWeight: "bold",
  },
}));

const EvalInput = ({
  config,
  source,
  disabled,
  record,
  categoryId = null,
  subCategoryId = null,
  subCategoryParamId = null,
  showCategoryTitle = true,
  showSubCategoryTitle = true,
  helperComponents = {},
}) => {
  const formState = useFormState();
  const classes = useStyles();
  const schema = useSchema(
    config,
    categoryId,
    subCategoryId,
    subCategoryParamId
  );
  const points = usePoints(config, schema, formState);
  return (
    <Grid container className={classes.root}>
      {schema.map((category) => (
        <Grid item xs={12} key={category.id}>
          {showCategoryTitle && (
            <Typography
              variant="h6"
              className={classNames(classes.padding, classes.title)}
            >
              {category.name}
            </Typography>
          )}
          {showCategoryTitle && <Divider />}
          {category.eval_sub_categories.map((subCategory) => (
            <Grid container key={subCategory.id}>
              <Grid item xs={8}>
                {showSubCategoryTitle && (
                  <Typography
                    variant="h6"
                    className={classNames(classes.padding, classes.subTitle)}
                  >
                    {subCategory.name}
                  </Typography>
                )}
                {showSubCategoryTitle && <Divider />}
                {subCategory.eval_sub_category_params.map(
                  (subCategoryParam) => (
                    <Fragment key={subCategoryParam.id}>
                      <Typography variant="body2" className={classes.padding}>
                        {subCategoryParam.name}
                      </Typography>
                      {((record) => {
                        const HelperComponent = get(
                          helperComponents,
                          subCategoryParam.id
                        );
                        return HelperComponent ? (
                          <HelperComponent record={record} />
                        ) : null;
                      })(record)}
                    </Fragment>
                  )
                )}
              </Grid>
              <Grid item xs={3}>
                <EvalRowInput
                  config={config}
                  source={source}
                  disabled={disabled}
                  classes={classes}
                  subCategory={subCategory}
                />
              </Grid>
              <Grid item xs={1}>
                <Typography
                  variant="h6"
                  className={classNames(
                    classes.paramPoints,
                    subCategory.id >= 21 && classes.paramPointsTwoLines
                  )}
                >
                  {get(points, subCategory.id, 0)}
                </Typography>
                <Divider />
              </Grid>
            </Grid>
          ))}
        </Grid>
      ))}

      <Grid item xs={11}>
        <Typography
          variant="h6"
          className={classNames(classes.padding, classes.title)}
        >
          Punteggio Totale
        </Typography>

        <Divider />
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="h6"
          className={classNames(classes.padding, classes.title, classes.total)}
        >
          {Object.keys(points).reduce((sum, id) => sum + get(points, id), 0)}
        </Typography>

        <Divider />
      </Grid>
    </Grid>
  );
};

export default EvalInput;
